import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type FilterOperatorParameters<TAttribute, TAttributeSet> = {
    name: string
    primaryAttribute: TAttribute | null
    attributeSet: TAttributeSet | null
}

export const filterOperatorParametersSchema = <TAttribute, TAttributeSet>(
    attributeSchema: z.ZodMiniType<TAttribute, TAttribute>,
    attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>,
) =>
    z.object({
        name: z.string(),
        primaryAttribute: z.nullable(attributeSchema),
        attributeSet: z.nullable(attributeSetSchema),
    })

function applyFilterOperator<T extends IndexingDim>(
    primaryAttribute: GeometryAttributeData<T>,
    attributeSet: GeometryAttributeSetData<T>,
): GeometryAttributeSetData<T> {
    const primaryAttrRefData = primaryAttribute.ref
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet)

    const filteredAttrRefDataSet = applyGeometryOperator(
        (primaryAttrRef: AttributeRef, attrRefSet: AttributeRefSet) => Operators.filter(primaryAttrRef, attrRefSet),
        primaryAttrRefData,
        attrRefDataSet,
    )

    return geometryAttributeRefDataSetToGeometryAttributeSetData(filteredAttrRefDataSet, primaryAttribute.dim)
}

export abstract class GeometryFilterOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, FilterOperatorParameters<TAttribute, TAttributeSet>> {
    protected override hasValidParameters(
        parameters: FilterOperatorParameters<TAttribute, TAttributeSet>,
    ): parameters is FilterOperatorParameters<TAttribute, TAttributeSet> & NonNullableParameters<FilterOperatorParameters<TAttribute, TAttributeSet>> {
        return parameters.primaryAttribute !== null && parameters.attributeSet !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<FilterOperatorParameters<TAttribute, TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {primaryAttribute, attributeSet} = parameters

        const [primaryAttributeDataValid, primaryAttributeDataInvalid] = scope.branch(evaluator.evaluate(primaryAttribute))
        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(evaluator.evaluate(attributeSet))

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(primaryAttributeDataValid, attributeSetDataValid),
            ([primaryAttributeData, attributeSetData]) => applyFilterOperator(primaryAttributeData, attributeSetData),
            "applyFilterOperator",
        )

        return {evaluatedGeometryData: geometryAttributeSetData, invalidator: scope.phi(primaryAttributeDataInvalid, attributeSetDataInvalid)}
    }
}
